import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {Paged} from "../interfaces/paged";
import {Client, ClientMergePreview, ClientRef, ClientStatus} from "../interfaces/client";
import {NbDialogService} from "@nebular/theme";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {ClientComponent} from "../clients/client.component";

@Injectable({
  providedIn: 'root'
})
export class ClientService extends APIService{

  public async findClients(page: number = 1, filters = null, sorts:{col: string, dir: number}[] = []): Promise<Paged<Client>>{
    return await this.post<any, Paged<Client>>('/clients/find', {
      page,
      sorts,
      filters
    });
  }

  public async getClient(id: number){
    return await this.get<Client>('/clients/' + id, 'Could not find client: ' + id);
  }

  public get emptyClient(): Client{
    return {
      status: ClientStatus.ACTIVE,
      name: null,
      description: null,
      matching_description: null,
      url: null,
      phone: null,
      email: null,
      address: null,
      id: null
    };
  }

  async addClient(client: Client): Promise<Client> {
    return await this.post<Client, Client>('/clients', client, client.name + ' added!', [409]);
  }

  async saveClient(id: number, updates:Partial<Client>, savemessage = null, silentStatuses: number[] = []) {
    return await this.put<Partial<Client>, Client>('/clients/' + id, updates, savemessage, silentStatuses);
  }

  /** What folding client `fromId` into client `intoId` would move; writes nothing. */
  async mergePreview(intoId: number, fromId: number): Promise<ClientMergePreview> {
    return await this.get<ClientMergePreview>(`/clients/${intoId}/merge/${fromId}`);
  }

  /** Fold client `fromId` into client `intoId`; `fromId` is deleted. Admin only. */
  async mergeClient(intoId: number, fromId: number): Promise<Client> {
    return await this.post<object, Client>(`/clients/${intoId}/merge/${fromId}`, {});
  }

  async deleteClient(id: number){
    return await this.delete('/clients/' + id, 'Client deleted');
  }

  /**
   * Every active/prospect client.
   */
  public async loadAllProspects(): Promise<ClientRef[]> {
    return this.loadAllByStatus([
      ClientStatus.ACTIVE,
      ClientStatus.PROSPECT,
      ClientStatus.PROSPECT_ON_HOLD,
      ClientStatus.PROSPECT_LOST,
    ]);
  }

  /**
   * Every client regardless of status, including archived. For name-collision
   * checks only (the broker quick-pick's dedup, #1194/#1217) - loadAllProspects()
   * excludes ARCHIVED on purpose (an archived client shouldn't appear as a
   * pickable option), but that same exclusion let a market source name matching
   * an archived Client go unrecognised and get silently duplicated.
   */
  public async loadAllClientsAnyStatus(): Promise<ClientRef[]> {
    return this.loadAllByStatus([
      ClientStatus.ARCHIVED,
      ClientStatus.ACTIVE,
      ClientStatus.PROSPECT,
      ClientStatus.PROSPECT_ON_HOLD,
      ClientStatus.PROSPECT_LOST,
    ]);
  }

  /** Every client with one of these statuses, id/name/status only, unpaged (picker source). */
  private async loadAllByStatus(status: ClientStatus[]): Promise<ClientRef[]> {
    return (await this.get<ClientRef[]>('/clients/lookup', null, { params: { 'status[]': status } })) ?? [];
  }

  async findSchedules(page: number, filters: any, sort: { col: string; dir: number }) {
    return await this.post<any, Paged<any>>('/clients/schedule', {
      page,
      sort,
      filters
    });
  }

  async findTimeline(buckets, page: number = 1, size: number = 40, filters = null, sort:{col: string, dir: number} = null): Promise<Paged<any>>{
    return await this.post<any, Paged<any>>('/clients/timeline', {
      size,
      page,
      sort,
      buckets,
      filters
    });
  }

  /**
   * Legacy archive/unarchive entry point used by the clients table and the
   * archive bin. Routes through the generic update endpoint (PUT /clients/:id).
   */
  async changeClientStatus(id: number, status: ClientStatus = ClientStatus.ACTIVE){
    return await this.setStatus(id, status);
  }

  /**
   * Lifecycle status transition via the generic update endpoint (PUT /clients/:id).
   * The backend ClientLifecycleService only permits legal transitions (e.g.
   * PROSPECT 2 -> ACTIVE 1 promote). Returns the updated Client.
   */
  async setStatus(id: number, status: ClientStatus): Promise<Client> {
    return await this.put<{status: ClientStatus}, Client>('/clients/' + id, { status }, 'Client ' + ClientStatus[status]);
  }

  async segments(id: number, from: string|Date, to: string|Date) {
    return await this.post<any, any>('/clients/'+id+'/timeline', {
      from,
      to
    });
  }

  /**
   * Open a dialog to create a new client.
   * Returns the created client or null if cancelled.
   */
  openCreateClientDialog(dialogService: NbDialogService, name?: string): Promise<Client | null> {
    return new Promise((resolve) => {
      const client = this.emptyClient;
      if (name) { client.name = name; }
      const sub = dialogService.open<PopupDialogComponent<ClientComponent>>(PopupDialogComponent, {
        closeOnBackdropClick: false,
        context: {
          comp: ClientComponent,
          componentContext: {
            client: client,
            editing: true,
            couldArchive: false
          }
        }
      }).onClose.subscribe((o: ClientComponent) => {
        let item: Client | null = null;
        if (o?.client?.id) {
          item = o.client;
        }
        if (sub) {
          sub.unsubscribe();
        }
        resolve(item);
      });
    });
  }
}
